import { useState } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import { ruleTrial } from '../settings.api';
import type {
  Dimension,
  ModuleAction,
  ModuleRefusal,
  ModuleView,
  NumberingRule,
  PostingRule,
  RulePreview,
  RulePreviewer,
} from '../settings.api';

export function ModulesView({
  modules,
  refusal,
  onAction,
}: {
  modules: readonly ModuleView[];
  refusal?: ModuleRefusal | null;
  onAction?: (code: string, action: ModuleAction, reason?: string) => void;
}): JSX.Element {
  const { t, language } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-modules">
      {refusal === null || refusal === undefined ? null : (
        <p className="alert alert--danger" role="alert" data-testid="module-refusal">
          {refusal.code}: {refusal.detail}
        </p>
      )}
      <div className="settings__cards">
        {modules.map((module) => (
          <article key={module.code} className="settings__card" data-testid={`module-${module.code}`} data-status={module.status}>
            <header className="settings__card-head">
              <span className="mono">{module.code}</span>
              <span className={module.status === 'active' ? 'pill pill--ok' : 'pill'}>{t(`settings.status.${module.status}`)}</span>
            </header>
            <h3>{module.nameI18n[language]}</h3>
            <p className="muted">
              {module.version} · {module.ownerTeam}
            </p>
            <p className="muted">{t(`settings.policy.${module.disablePolicy.split(':')[0] ?? module.disablePolicy}`)}</p>
            <div className="settings__card-actions">
              {module.status === 'installed' ? (
                <button type="button" className="btn btn--small" onClick={() => onAction?.(module.code, 'activate')}>
                  {t('settings.activate')}
                </button>
              ) : null}
              {module.status === 'active' ? (
                <>
                  <button type="button" className="btn btn--small" onClick={() => onAction?.(module.code, 'readonly', t('settings.readonlyReason'))}>
                    {t('settings.makeReadonly')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--small"
                    onClick={() => onAction?.(module.code, 'disable', t('settings.disableReason'))}
                  >
                    {t('settings.disable')}
                  </button>
                </>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function RuleTrialView({
  before,
  after,
  preview,
}: {
  before: PostingRule;
  after: PostingRule;
  preview?: RulePreview | null;
}): JSX.Element {
  const { t } = useI18n();
  const rows = ruleTrial(before, after);
  const line = (side: 'D' | 'C' | undefined): string => {
    if (side === undefined) return '—';
    return side === 'D' ? t('settings.rule.debit') : t('settings.rule.credit');
  };
  return (
    <div className="settings__trial" data-testid="rule-trial">
      <table className="table">
        <thead>
          <tr>
            <th>{t('settings.rule.line')}</th>
            <th>{t('settings.rule.before')}</th>
            <th>{t('settings.rule.after')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.index} data-changed={row.changed ? 'true' : 'false'}>
              <td className="mono">{String(row.index + 1)}</td>
              <td>
                {line(row.before?.side)} · <span className="mono">{row.before?.account ?? '—'}</span> ·{' '}
                <span className="mono">{row.before?.amount ?? '—'}</span>
              </td>
              <td>
                {line(row.after?.side)} · <span className="mono">{row.after?.account ?? '—'}</span> ·{' '}
                <span className="mono">{row.after?.amount ?? '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {preview === null || preview === undefined ? null : (
        <p className="muted" data-testid="rule-preview" data-balanced={preview.balanced ? 'true' : 'false'}>
          {t('settings.rule.difference')}: <span className="mono">{preview.difference}</span>
        </p>
      )}
    </div>
  );
}

export function PostingRulesView({
  rules,
  error,
  onPreview,
}: {
  rules: readonly PostingRule[];
  error?: string | null;
  onPreview?: RulePreviewer;
}): JSX.Element {
  const { t } = useI18n();
  const [selected, setSelected] = useState(0);
  const [draft, setDraft] = useState('');
  const [parsed, setParsed] = useState<PostingRule | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<RulePreview | null>(null);

  const current = rules[selected];
  if (current === undefined) {
    return (
      <div className="settings__panel" data-testid="settings-posting-rules">
        {error === null || error === undefined ? <p className="muted">{t('settings.empty')}</p> : <p className="alert alert--danger">{error}</p>}
      </div>
    );
  }

  const trial = (): void => {
    try {
      const lines = JSON.parse(draft === '' ? JSON.stringify(current.expression.lines) : draft) as PostingRule['expression']['lines'];
      const next: PostingRule = { ...current, expression: { lines } };
      setParsed(next);
      setParseError(null);
      if (onPreview !== undefined) {
        onPreview(next)
          .then(setPreview)
          .catch((cause: unknown) => setParseError(cause instanceof Error ? cause.message : String(cause)));
      }
    } catch (cause) {
      setParsed(null);
      setParseError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  return (
    <div className="settings__panel" data-testid="settings-posting-rules">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      <table className="table">
        <thead>
          <tr>
            <th>{t('settings.rule.code')}</th>
            <th>{t('settings.rule.documentType')}</th>
            <th>{t('settings.rule.operation')}</th>
            <th>{t('settings.rule.version')}</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((rule, index) => (
            <tr key={rule.code} data-testid="posting-rule" data-selected={index === selected ? 'true' : 'false'}>
              <td>
                <button type="button" className="link" onClick={() => setSelected(index)}>
                  {rule.code}
                </button>
              </td>
              <td>{rule.documentType}</td>
              <td>{rule.operationKind}</td>
              <td className="mono">{String(rule.version)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <label className="field">
        <span>{t('settings.rule.expression')}</span>
        <textarea
          className="input settings__editor"
          value={draft === '' ? JSON.stringify(current.expression.lines, null, 2) : draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
        />
      </label>
      {parseError === null ? null : (
        <p className="alert alert--danger" role="alert">
          {parseError}
        </p>
      )}
      <div className="settings__actions">
        <button type="button" className="btn" onClick={trial}>
          {t('settings.rule.trial')}
        </button>
      </div>
      {parsed === null ? null : <RuleTrialView before={current} after={parsed} preview={preview} />}
    </div>
  );
}

const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8];

export function DimensionsView({
  dimensions,
  error,
}: {
  dimensions: readonly Dimension[];
  error?: string | null;
}): JSX.Element {
  const { t, language } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-dimensions">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      <div className="settings__slots">
        {SLOTS.map((slot) => {
          const dimension = dimensions.find((item) => item.slot === slot);
          return (
            <article key={slot} className="settings__slot" data-testid={`dimension-slot-${String(slot)}`} data-empty={dimension === undefined ? 'true' : 'false'}>
              <span className="muted">
                {t('settings.dimensions.slot')} {String(slot)}
              </span>
              <strong>{dimension === undefined ? t('settings.dimensions.emptySlot') : dimension.nameI18n[language]}</strong>
              {dimension === undefined ? null : <span className="mono muted">{dimension.code}</span>}
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function NumberingView({ rules, error }: { rules: readonly NumberingRule[]; error?: string | null }): JSX.Element {
  const { t } = useI18n();
  const reset = (period: NumberingRule['resetPeriod']): string => t(`settings.numbering.reset.${period}`);
  return (
    <div className="settings__panel" data-testid="settings-numbering">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {rules.length === 0 ? (
        <p className="muted">{t('settings.empty')}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings.numbering.type')}</th>
              <th>{t('settings.numbering.prefix')}</th>
              <th>{t('settings.numbering.padding')}</th>
              <th>{t('settings.numbering.resetLabel')}</th>
              <th>{t('settings.numbering.next')}</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.typeCode} data-testid="numbering-rule">
                <td className="mono">{rule.typeCode}</td>
                <td className="mono">{rule.prefix}</td>
                <td className="mono">{String(rule.padding)}</td>
                <td>{reset(rule.resetPeriod)}</td>
                <td className="mono">{String(rule.nextNumber)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
